import {
  IFieldDefinition,
  TableColumnFieldElement,
} from "@entaconsulting/form";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import reorderDnD from "common/utils/reorderDnD";
import useDynamicField from "developer/edit/hooks/useDynamicField";
import { setColumnEdited } from "developer/edit/store/editorSlice";
import React, { useMemo } from "react";
import {
  DragDropContext,
  Draggable,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";
import { useAppDispatch } from "store/hooks";

type TableFieldRow = Pick<TableColumnFieldElement, "name"> & {
  isHidden: boolean;
};

const TableColumnsListForm: React.FC<{}> = () => {
  const dispatch = useAppDispatch();
  const {
    input: {
      value: { fields: tableFields },
      onChange: onChangeLayoutElement,
    },
  } =
    useDynamicField<{ fields: Omit<TableColumnFieldElement, "type">[] }>(
      "element.listFields"
    );
  const {
    input: { value: tableFieldsSchema, onChange: onChangeFieldDefinition },
  } = useDynamicField<Record<string, IFieldDefinition>>("fieldDef.schema");

  const onDragEnd = (result: DropResult) => {
    const { destination, source } = result;

    if (
      !destination ||
      (source.index === destination.index &&
        source.droppableId === destination.droppableId)
    ) {
      return;
    }

    const newOrderedFields = reorderDnD<Omit<TableColumnFieldElement, "type">>(
      tableFields,
      source.index,
      destination.index
    );

    onChangeLayoutElement({ target: { value: { fields: newOrderedFields } } });
  };

  const handleClickAddColumnField = (event: any) => {
    const newColumnLayoutElement: Omit<TableColumnFieldElement, "type"> = {
      name: `Column ${tableFields.length + 1}`,
      label: `Column ${tableFields.length + 1}`,
      size: 2,
    };

    const newColumnFieldDefinition: IFieldDefinition = {
      type: "text",
    };

    const newFields = [...tableFields, newColumnLayoutElement];

    onChangeLayoutElement({ target: { value: { fields: newFields } } });

    const newFieldDefinitions = { ...tableFieldsSchema };

    newFieldDefinitions[newColumnLayoutElement.name] = newColumnFieldDefinition;

    onChangeFieldDefinition({ target: { value: newFieldDefinitions } });
  };

  const handleClickDeleteColumnField = (fieldName: string) => (event: any) => {
    const newFields = tableFields.filter((field) => field.name !== fieldName);

    onChangeLayoutElement({ target: { value: { fields: newFields } } });

    const newFieldDefinitions = { ...tableFieldsSchema };

    delete newFieldDefinitions[fieldName];

    onChangeFieldDefinition({ target: { value: newFieldDefinitions } });
  };

  const handleClickHideColumnField =
    (fieldName: string, isHidden: boolean) => (event: any) => {
      let newFields: Omit<TableColumnFieldElement, "type">[];

      if (isHidden) {
        newFields = [
          ...tableFields,
          {
            name: fieldName,
            size: 2,
          },
        ];
      } else {
        newFields = tableFields.filter((x) => x.name !== fieldName);
      }

      onChangeLayoutElement({ target: { value: { fields: newFields } } });
    };

  const NoRows = () => {
    return (
      <Stack direction="row">
        <Typography variant="caption" textAlign="center" sx={{ width: "100%" }}>
          Table without columns
        </Typography>
      </Stack>
    );
  };

  const visibleRows: TableFieldRow[] = useMemo(() => {
    return (tableFields ?? []).map(
      (field: Omit<TableColumnFieldElement, "type">) => {
        return {
          name: field.name,
          isHidden: false,
        };
      }
    );
  }, [tableFields]);

  const hiddenRows: TableFieldRow[] = useMemo(() => {
    return Object.keys(tableFieldsSchema)
      .filter((fieldName) => {
        return !visibleRows.find((item) => item.name === fieldName);
      })
      .map((fieldName) => ({
        name: fieldName,
        size: 2,
        isHidden: true,
      }));
  }, [tableFieldsSchema, visibleRows]);

  const rows: TableFieldRow[] = useMemo(
    () => [...visibleRows, ...hiddenRows],
    [visibleRows, hiddenRows]
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Button
          color="inherit"
          startIcon={<AddIcon />}
          size="small"
          onClick={handleClickAddColumnField}
        >
          Add Column
        </Button>
      </Grid>
      <Grid item xs={12}>
        {rows.length === 0 ? (
          <NoRows />
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="droppable">
              {(droppableProvider) => (
                <div
                  {...droppableProvider.droppableProps}
                  ref={droppableProvider.innerRef}
                >
                  {rows.map((field, index) => (
                    <Draggable
                      key={field.name}
                      draggableId={field.name}
                      index={index}
                      isDragDisabled={field.isHidden}
                    >
                      {(draggableProvider) => (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          gap={1}
                          {...draggableProvider.draggableProps}
                          {...draggableProvider.dragHandleProps}
                          ref={draggableProvider.innerRef}
                        >
                          <Box>
                            <IconButton
                              size="small"
                              disabled={field.isHidden}
                              sx={{ cursor: "grab" }}
                            >
                              <DragIndicatorIcon />
                            </IconButton>

                            <Link
                              underline="hover"
                              variant="subtitle2"
                              sx={{ cursor: "pointer" }}
                              noWrap
                              onClick={() =>
                                dispatch(
                                  setColumnEdited({ index, name: field.name })
                                )
                              }
                            >
                              {field.name}
                            </Link>
                          </Box>
                          <Box>
                            <Tooltip
                              title={`${
                                field.isHidden ? "Show Column" : "Hide Column"
                              }`}
                              onClick={handleClickHideColumnField(
                                field.name,
                                field.isHidden
                              )}
                            >
                              <IconButton size="small">
                                {field.isHidden ? (
                                  <VisibilityOffIcon />
                                ) : (
                                  <VisibilityIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="Delete Column"
                              onClick={handleClickDeleteColumnField(field.name)}
                            >
                              <IconButton size="small">
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Stack>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvider.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Grid>
    </Grid>
  );
};

export default TableColumnsListForm;
